import { bankLabel } from '../../utils/bank';
import React, { useState } from 'react';
import SupervisorLayout from '../../components/SupervisorLayout';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import { fetchSupervisorResumeBanqueLottery } from '../../api/supervisor';
import { todayISO } from '../../utils/date';
import './SupervisorResumeBanqueLotteryPage.css';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Champ Banque aligne sur le meme style que Loterie (texte + icone 🔍 + modal
// de recherche + nom resolu AU BLUR, pas seulement via le modal — demande
// explicite 2026-09-17).
const SupervisorResumeBanqueLotteryPage = () => {
  const [bankId, setBankId] = useState('');
  const [bankName, setBankName] = useState('');
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  const [date, setDate] = useState(todayISO());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!bankId || !lottery) { setError('Choisis une banque et une loterie.'); return; }
    setLoading(true);
    setError('');
    try {
      setData(await fetchSupervisorResumeBanqueLottery({ bank: bankId, tirage: lottery, date }));
    } catch (err) {
      setError('Recherche impossible.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SupervisorLayout>
      <h2 className="svrbl-title">Résumé de la Banque-Lottery</h2>

      <div className="svrbl-card">
        <form onSubmit={handleSearch} className="svrbl-form">
          <div className="svrbl-row">
            <label className="svrbl-label">Banque</label>
            <SupervisorBankCodeField
              value={bankId}
              name={bankName}
              onChange={(id) => { setBankId(id); setBankName(''); }}
              onResolve={(id, nm) => { setBankId(id); setBankName(nm); }}
              inputClass="svrbl-input svrbl-input-sm"
              iconClass="svrbl-icon-btn"
              nameClass="svrbl-name"
            />
          </div>

          <div className="svrbl-row">
            <label className="svrbl-label">Loterie</label>
            <LotteryCodeField
              value={lottery}
              name={lotteryName}
              onChange={(code) => { setLottery(code); setLotteryName(''); }}
              onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
              inputClass="svrbl-input svrbl-input-sm"
              iconClass="svrbl-icon-btn"
              nameClass="svrbl-name"
            />
          </div>

          <div className="svrbl-row">
            <label className="svrbl-label">Date</label>
            <DateField className="svrbl-input" value={date} onChange={setDate} />
          </div>

          {error && <p className="svrbl-error">{error}</p>}
          <button type="submit" className="svrbl-search-btn" disabled={loading}>
            {loading ? 'Recherche...' : 'Obtenir'}
          </button>
        </form>
      </div>

      {data && (
        <div className="svrbl-receipt">
          <p className="svrbl-title-line">Résumé {data.date}</p>
          <p className="svrbl-bank-line">Banca : {bankLabel(data.bank_seq, data.bank_name)}{data.bank_username ? ` (${data.bank_username})` : ''}</p>

          {data.lotteries.length === 0 && <p className="svrbl-empty">Aucune vente ce jour pour cette banque.</p>}

          {data.lotteries.map((lot) => (
            <div key={lot.tirage_code} className={`svrbl-lottery-block${lot.selected ? ' svrbl-lottery-selected' : ''}`}>
              <p className="svrbl-lottery-title">{lot.tirage_code} =&gt; {lot.tirage_name}</p>

              <div className="svrbl-line">
                <span>NUM : {lot.numbers ?? '—'}</span>
                <span>Fiches Vendu : {lot.fiches_vendu}</span>
              </div>
              <div className="svrbl-line">
                <span>WIN4 : {lot.win4 ?? '—'}</span>
                <span>Total Paris : {lot.total_paris}</span>
              </div>
              <div className="svrbl-amount-row">
                <span>Vendu =</span>
                <span>{money(lot.vendu)}</span>
              </div>

              {lot.selected && (
                <>
                  <div className="svrbl-amount-row">
                    <span>Gagne =</span>
                    <span>{money(lot.gagne)}</span>
                  </div>
                  <hr className="svrbl-dash" />
                  <div className="svrbl-amount-row svrbl-bold">
                    <span>Brut =</span>
                    <span>{money(lot.brut)}</span>
                  </div>

                  <p className="svrbl-section-title">-------Ticket Gagner-------</p>
                  {lot.tickets_gagnants.length === 0 ? (
                    <p className="svrbl-empty-sm">Aucun ticket gagnant.</p>
                  ) : (
                    <table className="svrbl-ticket-table">
                      <thead>
                        <tr><th>Temp</th><th>T/No.</th><th>Produit</th><th>Numero</th><th>Mise</th><th>Gagne</th></tr>
                      </thead>
                      <tbody>
                        {lot.tickets_gagnants.map((t, i) => (
                          <tr key={i}>
                            <td>{t.time}</td>
                            <td>{t.online ? 'O' : 'L'} | {t.ticket_no ?? '—'}</td>
                            <td>{t.product_abbr}</td>
                            <td>{t.numbers}</td>
                            <td>{money(t.mise)}</td>
                            <td>{money(t.gagne)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              <hr className="svrbl-dotted" />
            </div>
          ))}

          <hr className="svrbl-solid" />
          <p className="svrbl-section-title svrbl-bold">Généraux</p>
          <div className="svrbl-amount-row">
            <span>Vendu =</span>
            <span>{money(data.generaux.vendu)}</span>
          </div>
          <div className="svrbl-amount-row">
            <span>Gagne =</span>
            <span>{money(data.generaux.gagne)}</span>
          </div>
          <hr className="svrbl-dash" />
          <div className="svrbl-amount-row svrbl-bold">
            <span>Brut =</span>
            <span>{money(data.generaux.brut)}</span>
          </div>
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorResumeBanqueLotteryPage;
